"use client";

import * as React from "react";

export type AppearancePreviewVariant = "light" | "dark" | "system";

/* eslint-disable no-restricted-syntax -- Art DEPICTING a light and a dark
   interface. The colours must not follow the current theme: a Light card has to
   look light while the app is dark, which is the entire point of the preview.
   Semantic tokens would make every card wear whatever is already on screen. */
const PALETTE = {
  light: {
    chrome: "#e8e8ec",
    page: "#ffffff",
    edge: "#d3d3db",
    line: "#c2c2cc",
    quiet: "#dcdce4",
  },
  dark: {
    chrome: "#121215",
    page: "#1f1f24",
    edge: "#34343c",
    line: "#4a4a55",
    quiet: "#33333c",
  },
} as const;
const ACCENT = "#5b5bd6";
/* eslint-enable no-restricted-syntax */

const WIDTH = 200;
const HEIGHT = 132;
const RAIL = 62;

/** One mock window, in a single palette. Clipped by the caller for `system`. */
function Panel({ tone }: { tone: keyof typeof PALETTE }): React.ReactElement {
  const c = PALETTE[tone];
  const bar = (x: number, y: number, w: number, fill: string) => (
    <rect key={`${x}-${y}-${w}`} fill={fill} height={5} rx={2.5} width={w} x={x} y={y} />
  );
  return (
    <g>
      <rect fill={c.chrome} height={HEIGHT} width={WIDTH} x={0} y={0} />
      {/* the rail: an identity tile over a short stack of rows */}
      <rect fill={ACCENT} height={13} rx={4} width={13} x={12} y={13} />
      {bar(12, 38, 30, c.line)}
      {bar(12, 50, 22, c.quiet)}
      {bar(12, 62, 26, c.quiet)}
      {bar(12, 74, 20, c.quiet)}
      {/* the page: one island cut out of the chrome, as the shell does. It
          reads by its EDGE as much as its fill — which is true of the real
          thing, and is what stops the two planes merging in the miniature. */}
      <rect
        fill={c.page}
        height={HEIGHT - 24}
        rx={7}
        stroke={c.edge}
        strokeWidth={1}
        width={WIDTH - RAIL - 12}
        x={RAIL}
        y={12}
      />
      {bar(RAIL + 12, 26, 44, c.line)}
      <rect fill={ACCENT} height={8} rx={4} width={26} x={WIDTH - 50} y={24.5} />
      {bar(RAIL + 12, 44, 92, c.quiet)}
      {bar(RAIL + 12, 60, 72, c.quiet)}
      {bar(RAIL + 12, 76, 100, c.quiet)}
      {bar(RAIL + 12, 92, 60, c.quiet)}
    </g>
  );
}

/**
 * A miniature interface, drawn rather than photographed.
 *
 * SVG so it stays sharp at any size and on any display — the bitmap this
 * replaced was authored at one width and went soft the moment the card grew.
 * It also means the art has no asset to keep in sync with the design.
 *
 * `system` is the light panel with the dark one clipped over its right half:
 * the only honest way to draw "whichever one the machine is using".
 */
export function AppearancePreviewArt({
  variant,
}: {
  variant: AppearancePreviewVariant;
}): React.ReactElement {
  const clipId = React.useId();
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      role="presentation"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      width="100%"
    >
      {variant === "system" ? (
        <>
          <defs>
            <clipPath id={clipId}>
              <rect height={HEIGHT} width={WIDTH / 2} x={WIDTH / 2} y={0} />
            </clipPath>
          </defs>
          <Panel tone="light" />
          <g clipPath={`url(#${clipId})`}>
            <Panel tone="dark" />
          </g>
        </>
      ) : (
        <Panel tone={variant} />
      )}
    </svg>
  );
}
